import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/config";
import { refreshSettings } from "@/lib/useSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Eye, Sparkles, Save, Send, CalendarClock, Link2, Copy } from "lucide-react";
import MaintenancePage from "@/pages/MaintenancePage";
import SessionTypesEditor from "./SessionTypesEditor";
import EmailDomainWidget from "./EmailDomainWidget";

export default function SettingsAdminTab() {
  const [form, setForm] = useState({
    maintenance_mode: false,
    maintenance_title: "",
    maintenance_message: "",
    maintenance_eta: "",
    telegram_group_url: "",
    telegram_channel_username: "",
    telegram_discussion_post: "",
    telegram_min_tier: "workshop",
    ghl_calendar_url: "",
    ghl_webhook_secret: "",
    zoom_default_meeting_url: "",
    zoom_account_id: "",
    zoom_client_id: "",
    zoom_client_secret: "",
  });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings");
    // Public /settings redacts admin secrets; fetch full via admin endpoint.
    let admin = null;
    try {
      const r = await api.get("/admin/settings/full");
      admin = r?.data || null;
    } catch { /* endpoint not required; secrets stay hidden */ }
    setForm({
      maintenance_mode: !!data.maintenance_mode,
      maintenance_title: data.maintenance_title || "",
      maintenance_message: data.maintenance_message || "",
      maintenance_eta: data.maintenance_eta || "",
      telegram_group_url: data.telegram_group_url || "",
      telegram_channel_username: (data.telegram_channel_username || "").replace(/^@/, ""),
      telegram_discussion_post: data.telegram_discussion_post || "",
      telegram_min_tier: data.telegram_min_tier || "workshop",
      ghl_calendar_url: data.ghl_calendar_url || "",
      ghl_webhook_secret: admin?.ghl_webhook_secret || "",
      zoom_default_meeting_url: data.zoom_default_meeting_url || admin?.zoom_default_meeting_url || "",
      zoom_account_id: admin?.zoom_account_id || "",
      zoom_client_id: admin?.zoom_client_id || "",
      zoom_client_secret: admin?.zoom_client_secret || "",
    });
  };
  useEffect(() => { load(); }, []);

  const save = async (overrides = {}) => {
    setSaving(true);
    try {
      const payload = { ...form, ...overrides };
      // Normalize telegram username — strip @ and any https:// prefix
      payload.telegram_channel_username = (payload.telegram_channel_username || "")
        .replace(/^https?:\/\/(t\.me|telegram\.me)\//, "")
        .replace(/^@/, "")
        .trim();
      await api.patch("/admin/settings", payload);
      setForm(payload);
      await refreshSettings();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl">Settings</h2>
        <p className="text-sm text-white/55 mt-1">
          Control site-wide behavior. Only admins are unaffected by maintenance mode — you can keep uploading, editing, and testing while everyone else sees the splash.
        </p>
      </div>

      {/* Maintenance card */}
      <div className={`glass rounded-2xl p-6 ${form.maintenance_mode ? "border-[#E52E71]/40" : ""}`}>
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${
            form.maintenance_mode ? "bg-[#E52E71]/20 text-[#E52E71]" : "bg-[#FF8A00]/15 text-[#FF8A00]"
          }`}>
            <AlertTriangle size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-display text-xl">Maintenance mode</div>
                <div className="text-sm text-white/55 mt-1">
                  {form.maintenance_mode
                    ? <span className="text-[#E52E71]">Live: members see the splash. You keep full admin access.</span>
                    : <span className="text-white/50">Off — site behaves normally for everyone.</span>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-white/50">{form.maintenance_mode ? "On" : "Off"}</span>
                <Switch
                  checked={form.maintenance_mode}
                  onCheckedChange={(v) => save({ maintenance_mode: v })}
                  disabled={saving}
                  data-testid="maintenance-toggle"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4">
          <div>
            <Label className="text-xs text-white/60">Headline</Label>
            <Input value={form.maintenance_title}
              onChange={(e) => setForm({...form, maintenance_title: e.target.value})}
              data-testid="maintenance-title"
              placeholder="Rewiring the lab."
              className="bg-[#0F0F0F] border-white/10 mt-1 font-display text-lg" />
          </div>
          <div>
            <Label className="text-xs text-white/60">Message</Label>
            <Textarea value={form.maintenance_message}
              onChange={(e) => setForm({...form, maintenance_message: e.target.value})}
              data-testid="maintenance-message"
              rows={3}
              placeholder="Dr. Slava is uploading new peptide protocols. Back within 30 min."
              className="bg-[#0F0F0F] border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-white/60">ETA (optional pill)</Label>
            <Input value={form.maintenance_eta}
              onChange={(e) => setForm({...form, maintenance_eta: e.target.value})}
              data-testid="maintenance-eta"
              placeholder="Back at 6:00 PM PT"
              className="bg-[#0F0F0F] border-white/10 mt-1" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => save()} disabled={saving} data-testid="save-settings-btn"
              className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
              <Save size={14} className="mr-2" /> Save copy
            </Button>
            <Button onClick={() => setPreview(true)} variant="outline"
              data-testid="preview-maintenance-btn"
              className="rounded-full bg-transparent border-white/10">
              <Eye size={14} className="mr-2" /> Preview splash
            </Button>
          </div>
        </div>
      </div>

      {/* Telegram community card */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 bg-[#229ED9]/15 text-[#229ED9]">
            <Send size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl">Telegram community</div>
            <div className="text-sm text-white/55 mt-1">
              Embed a live Telegram group into the Community tab. Members will see a big "Open in Telegram" CTA plus a live discussion feed from your public channel.
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs text-white/60">Telegram group invite URL</Label>
            <Input value={form.telegram_group_url}
              onChange={(e) => setForm({...form, telegram_group_url: e.target.value})}
              data-testid="telegram-group-url"
              placeholder="https://t.me/+abc123... or https://t.me/yourgroup"
              className="bg-[#0F0F0F] border-white/10 mt-1" />
            <p className="text-[11px] text-white/40 mt-1">The "Open in Telegram" button opens this link. Paste any t.me/... invite or public group URL.</p>
          </div>
          <div>
            <Label className="text-xs text-white/60">Public channel username</Label>
            <Input value={form.telegram_channel_username}
              onChange={(e) => setForm({...form, telegram_channel_username: e.target.value})}
              data-testid="telegram-channel-username"
              placeholder="drthrive365 (no @)"
              className="bg-[#0F0F0F] border-white/10 mt-1" />
            <p className="text-[11px] text-white/40 mt-1">Enables the embedded discussion feed. Requires a public channel (not a private group).</p>
          </div>
          <div>
            <Label className="text-xs text-white/60">Discussion widget anchor (optional)</Label>
            <Input value={form.telegram_discussion_post}
              onChange={(e) => setForm({...form, telegram_discussion_post: e.target.value})}
              data-testid="telegram-discussion-post"
              placeholder="drthrive365/1"
              className="bg-[#0F0F0F] border-white/10 mt-1" />
            <p className="text-[11px] text-white/40 mt-1">Pin the widget to a specific post like <span className="font-mono">channel/postid</span>. Leave blank to show the channel feed.</p>
          </div>
          <div>
            <Label className="text-xs text-white/60">Minimum tier to access</Label>
            <Select value={form.telegram_min_tier}
              onValueChange={(v) => setForm({...form, telegram_min_tier: v})}>
              <SelectTrigger className="bg-[#0F0F0F] border-white/10 mt-1" data-testid="telegram-min-tier">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="free">Free (everyone logged in)</SelectItem>
                <SelectItem value="workshop">Workshop and above</SelectItem>
                <SelectItem value="course">Course and above</SelectItem>
                <SelectItem value="membership">Membership and above</SelectItem>
                <SelectItem value="cohort">Cohort only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-2">
            <Button onClick={() => save()} disabled={saving} data-testid="save-telegram-btn"
              className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
              <Save size={14} className="mr-2" /> Save Telegram settings
            </Button>
            {form.telegram_group_url && (
              <Button variant="outline" asChild
                className="rounded-full bg-transparent border-white/10">
                <a href={form.telegram_group_url} target="_blank" rel="noreferrer">
                  Test link ↗
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 text-sm text-white/55 flex items-start gap-3">
        <Sparkles size={16} className="text-[#FF8A00] mt-0.5 shrink-0" />
        <p>
          <strong className="text-white/80">Tip:</strong> members are polled every 30 seconds — turning maintenance off will drop them straight back onto their dashboard without a reload.
        </p>
      </div>

      {/* GHL booking flow */}
      <div className="glass rounded-2xl p-6" data-testid="ghl-booking-card">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 bg-[#FF8A00]/15 text-[#FF8A00]">
            <CalendarClock size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl">Booking scheduling (GoHighLevel)</div>
            <div className="text-sm text-white/55 mt-1">
              After a customer pays, admins email them <em>this</em> GHL calendar link from the Intakes tab. When they pick a time in GHL, either paste it manually or wire the webhook below into your GHL workflow to auto-populate the slot.
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4">
          <div>
            <Label className="text-xs text-white/60">GHL calendar URL (shared)</Label>
            <Input value={form.ghl_calendar_url}
              onChange={(e) => setForm({...form, ghl_calendar_url: e.target.value})}
              data-testid="ghl-calendar-url"
              placeholder="https://api.leadconnectorhq.com/widget/booking/xxxxx"
              className="bg-[#0F0F0F] border-white/10 mt-1" />
            <p className="text-[11px] text-white/40 mt-1">
              We append <span className="font-mono">?intake_id=...</span> when we email the link so the webhook can match the booking to the right intake.
            </p>
          </div>
          <div>
            <Label className="text-xs text-white/60">GHL webhook secret (optional but recommended)</Label>
            <div className="relative mt-1">
              <Input value={form.ghl_webhook_secret}
                onChange={(e) => setForm({...form, ghl_webhook_secret: e.target.value})}
                data-testid="ghl-webhook-secret"
                placeholder="Paste any long random string; add it as `secret` in the GHL webhook body"
                className="bg-[#0F0F0F] border-white/10 pr-24" />
              <Button
                type="button"
                onClick={() => setForm({...form, ghl_webhook_secret: crypto.randomUUID().replace(/-/g,'') + crypto.randomUUID().replace(/-/g,'')})}
                data-testid="ghl-generate-secret"
                variant="outline"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 rounded-full bg-transparent border-white/10 text-xs px-3"
              >
                Generate
              </Button>
            </div>
            <p className="text-[11px] text-white/40 mt-1">
              Without a secret the webhook is disabled. GHL must POST this exact value in the JSON body under <span className="font-mono">"secret"</span>.
            </p>
          </div>

          <div className="rounded-xl border border-white/5 bg-[#0F0F0F] p-4 text-xs text-white/60 space-y-2">
            <div className="text-[11px] tracking-[0.2em] uppercase text-white/40 flex items-center gap-2"><Link2 size={11}/> GHL webhook endpoint</div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="font-mono text-[#FF8A00] break-all">{`${window.location.origin}/api/ghl/booking-webhook`}</code>
              <Button size="sm" variant="outline"
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/api/ghl/booking-webhook`); toast.success("Copied"); }}
                data-testid="ghl-copy-webhook-url"
                className="h-7 rounded-full bg-transparent border-white/10 text-[11px]">
                <Copy size={10} className="mr-1" /> Copy
              </Button>
            </div>
            <div className="text-[11px] text-white/45">
              Expected JSON body: <span className="font-mono">{`{ "secret": "…", "intake_id": "…", "email": "…", "scheduled_at": "2026-03-01T15:00:00Z", "duration_min": 60 }`}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => save()} disabled={saving} data-testid="save-ghl-btn"
              className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
              <Save size={14} className="mr-2" /> Save booking settings
            </Button>
            {form.ghl_calendar_url && (
              <Button variant="outline" asChild
                className="rounded-full bg-transparent border-white/10">
                <a href={form.ghl_calendar_url} target="_blank" rel="noreferrer">
                  Test calendar link ↗
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Zoom */}
      <div className="glass rounded-2xl p-6" data-testid="zoom-settings-card">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 bg-[#FF8A00]/15 text-[#FF8A00]">
            <Sparkles size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl">Zoom auto-provisioning</div>
            <div className="text-sm text-white/55 mt-1">
              When admins approve a booking, Dr Thrive can auto-generate a Zoom meeting instead of pasting one in. Fill in the S2S OAuth credentials from your Zoom Marketplace app — otherwise the default meeting URL below is used as a fallback.
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4">
          <div>
            <Label className="text-xs text-white/60">Default meeting URL (fallback)</Label>
            <Input value={form.zoom_default_meeting_url}
              onChange={(e) => setForm({...form, zoom_default_meeting_url: e.target.value})}
              data-testid="zoom-default-url"
              placeholder="https://zoom.us/j/1234567890?pwd=..."
              className="bg-[#0F0F0F] border-white/10 mt-1" />
            <p className="text-[11px] text-white/40 mt-1">
              Used automatically when the Zoom API isn't configured yet. Your Personal Meeting Room URL works well.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-white/60">Zoom Account ID</Label>
              <Input value={form.zoom_account_id}
                onChange={(e) => setForm({...form, zoom_account_id: e.target.value})}
                data-testid="zoom-account-id"
                placeholder="Account ID from Zoom Marketplace"
                className="bg-[#0F0F0F] border-white/10 mt-1"/>
            </div>
            <div>
              <Label className="text-xs text-white/60">Client ID</Label>
              <Input value={form.zoom_client_id}
                onChange={(e) => setForm({...form, zoom_client_id: e.target.value})}
                data-testid="zoom-client-id"
                placeholder="Client ID"
                className="bg-[#0F0F0F] border-white/10 mt-1"/>
            </div>
            <div>
              <Label className="text-xs text-white/60">Client Secret</Label>
              <Input type="password" value={form.zoom_client_secret}
                onChange={(e) => setForm({...form, zoom_client_secret: e.target.value})}
                data-testid="zoom-client-secret"
                placeholder="Client Secret"
                className="bg-[#0F0F0F] border-white/10 mt-1"/>
            </div>
          </div>
          <div className="rounded-xl border border-white/5 bg-[#0F0F0F] p-4 text-[11px] text-white/55 leading-relaxed">
            Create a <strong className="text-white/80">Server-to-Server OAuth</strong> app in the Zoom Marketplace. Required scopes: <span className="font-mono">meeting:write:admin</span>, <span className="font-mono">user:read:admin</span>. Activate the app and copy the three values above.
          </div>
          <div>
            <Button onClick={() => save()} disabled={saving} data-testid="save-zoom-btn"
              className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
              <Save size={14} className="mr-2"/> Save Zoom settings
            </Button>
          </div>
        </div>
      </div>

      {/* Session types editor (also lives at the top of the Bookings tab) */}
      <SessionTypesEditor
        compact
        title="Session types & pricing"
        subtitle="Update the price and length for each 1-on-1 session. Changes go live instantly on the public Book a Session page."
      />

      {/* Resend email domain management */}
      <div className="pt-2 border-t border-white/5">
        <EmailDomainWidget />
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-[#050505]" data-testid="maintenance-preview">
          <button
            onClick={() => setPreview(false)}
            data-testid="close-preview-btn"
            className="absolute top-6 right-6 z-10 text-xs tracking-[0.25em] uppercase text-white/60 hover:text-white bg-white/5 border border-white/10 rounded-full px-4 py-2"
          >
            Close preview
          </button>
          <MaintenancePage
            settings={form}
            isAdmin={true}
            onOpenSite={() => setPreview(false)}
          />
        </div>
      )}
    </div>
  );
}
